import React, { useState, useEffect, useRef } from 'react';
import { Search, ArrowLeft, ArrowRight, Calendar, Filter, MoreHorizontal, Edit, Trash2,
  Scissors, AlignJustify, CalendarDays, User, Clock, ChevronLeft, CalendarRange, CalendarX2, Loader2, AlertTriangle, Download } from 'lucide-react';
import { useTimezone } from '../../../../../hooks/useTimezone';
import { formatInTimezone, formatTimeOnly, formatDateOnly } from '../../../../../utils/timezoneUtils';
import SplitEntryModal from './SplitEntryModal';
import { useSocket } from '../../../../../hooks/useSocket';
import { useAuth } from '../../../../../context/AuthContext';
import ExportModal from './ExportModal';
import DownloadProgressToast from '../../../../../components/DownloadProgressToast';

// Helper function to get today's date range
const getTodayDateRange = () => {
  const today = new Date();
  
  // Start of day (12:00 AM)
  const startOfDay = new Date(today);
  startOfDay.setHours(0, 0, 0, 0);
  
  // End of day (11:59 PM)
  const endOfDay = new Date(today);
  endOfDay.setHours(23, 59, 59, 999);
  
  return {
    start: startOfDay.toISOString(),
    end: endOfDay.toISOString(),
    displayDate: today
  };
};

// Helper function to get past 7 days date range
const getPast7DaysRange = () => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 6); // 6 days ago + today = 7 days
  
  // Start of 7 days ago
  const startOfRange = new Date(sevenDaysAgo);
  startOfRange.setHours(0, 0, 0, 0);
  
  // End of today
  const endOfRange = new Date(today);
  endOfRange.setHours(23, 59, 59, 999);
  
  return {
    start: startOfRange.toISOString(),
    end: endOfRange.toISOString(),
    displayDate: today
  };
};

// Helper function to get past month date range
const getPastMonthRange = () => {
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  
  // Start of one month ago
  const startOfRange = new Date(oneMonthAgo);
  startOfRange.setHours(0, 0, 0, 0);
  
  // End of today
  const endOfRange = new Date(today);
  endOfRange.setHours(23, 59, 59, 999);
  
  return {
    start: startOfRange.toISOString(),
    end: endOfRange.toISOString(),
    displayDate: today
  };
};

const EmployeeTab = () => {
  const userTimezone = useTimezone();
  const { currentUser } = useAuth();
  const { socket } = useSocket(currentUser?.id);

  // View state: 'employees' or 'timeline'
  const [currentView, setCurrentView] = useState('employees');
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Employee list state
  const [employees, setEmployees] = useState([]);
  const [employeeEntries, setEmployeeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Date range state
  const [currentDateView, setCurrentDateView] = useState(getTodayDateRange());
  const [dateRangeType, setDateRangeType] = useState('today'); // 'today', 'past7days', 'pastmonth', 'custom'
  const [filters, setFilters] = useState({
    start_date: getTodayDateRange().start.split('T')[0],
    end_date: getTodayDateRange().end.split('T')[0]
  });

  // Custom date range inputs
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Timeline state
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);

  // Batched loading state
  const [batchLoading, setBatchLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [showLargeDatasetModal, setShowLargeDatasetModal] = useState(false);
  const [selectedRecordLimit, setSelectedRecordLimit] = useState(null);
  const [allEntries, setAllEntries] = useState([]);
  const [displayEntries, setDisplayEntries] = useState([]);

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const abortControllerRef = useRef(null);

  // Column definitions for export
  const allColumns = [
    { id: 'id', label: 'ID', type: 'integer' },
    { id: 'employee_id', label: 'Employee ID', type: 'integer' },
    { id: 'full_name', label: 'Full Name', type: 'select' },
    { id: 'activity', label: 'Activity', type: 'select' },
    { id: 'business_unit', label: 'Business Unit', type: 'select' },
    { id: 'clock_in_time', label: 'Clock In', type: 'datetime' },
    { id: 'clock_out_time', label: 'Clock Out', type: 'datetime' },
    { id: 'total_time', label: 'Total Time', type: 'time' },
    { id: 'group', label: 'Group', type: 'text' },
    { id: 'project', label: 'Project', type: 'text' },
    { id: 'shift', label: 'Shift', type: 'text' },
    { id: 'team', label: 'Team', type: 'text' },
    { id: 'date', label: 'Date', type: 'date' },
    { id: 'location', label: 'Location', type: 'text' },
    { id: 'notes', label: 'Notes', type: 'text' },
    { id: 'reference1', label: 'Reference 1', type: 'text' },
    { id: 'reference2', label: 'Reference 2', type: 'text' },
    { id: 'reference3', label: 'Reference 3', type: 'text' },
  ];

  // Custom date range handler
  const handleCustomDateRangeSearch = () => {
    if (!customStartDate || !customEndDate) {
      alert('Please select both start and end dates');
      return;
    }
    
    if (new Date(customStartDate) > new Date(customEndDate)) {
      alert('Start date cannot be after end date');
      return;
    }
    
    // Create date range from custom inputs
    const startOfRange = new Date(customStartDate);
    startOfRange.setHours(0, 0, 0, 0);
    
    const endOfRange = new Date(customEndDate);
    endOfRange.setHours(23, 59, 59, 999);
    
    const newDateRange = {
      start: startOfRange.toISOString(),
      end: endOfRange.toISOString(),
      displayDate: new Date(customEndDate) // Use end date for display
    };
    
    setCurrentDateView(newDateRange);
    setDateRangeType('custom');

    updateFilters({
      start_date: customStartDate,
      end_date: customEndDate
    });
  };

  // Handler for date range buttons
  const handleDateRangeChange = (rangeType) => {
    let newDateRange;
    
    switch (rangeType) {
      case 'today':
        newDateRange = getTodayDateRange();
        break;
      case 'past7days':
        newDateRange = getPast7DaysRange();
        break;
      case 'pastmonth':
        newDateRange = getPastMonthRange();
        break;
      default:
        return;
    }
    
    setCurrentDateView(newDateRange);
    setDateRangeType(rangeType);
    
    updateFilters({
      start_date: newDateRange.start.split('T')[0],
      end_date: newDateRange.end.split('T')[0]
    });
  };

  // Helper function to update filters
  const updateFilters = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  };

  // Listen for export job updates via WebSocket
  useEffect(() => {
    if (!socket) return;

    const handleExportUpdate = (data) => {
      // Only update if we're tracking this job
      if (downloadProgress && downloadProgress.jobId === data.jobId) {
        setDownloadProgress(prev => ({
          ...prev,
          status: data.status === 'completed' ? 'completed' : data.status === 'failed' ? 'error' : 'downloading',
          progress: data.progress || 0,
          processedRows: data.processedRows || 0,
          totalRows: data.totalRows || 0,
          filename: data.filename || prev.filename,
          error: data.error
        }));

        // If completed via WebSocket, trigger download
        if (data.status === 'completed') {
          // Fetch the file with authentication
          fetch(`/api/labor-export/${data.jobId}/download`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
          .then(response => response.blob())
          .then(blob => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', data.filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setDownloadProgress(prev => ({
              ...prev,
              status: 'completed',
              progress: 100
            }));

            setTimeout(() => {
              setDownloadProgress(null);
            }, 3000);
          })
          .catch(error => {
            console.error('Error downloading file:', error);
            setDownloadProgress(prev => ({
              ...prev,
              status: 'error',
              error: 'Failed to download file'
            }));
          });
        }

        // If failed via WebSocket
        if (data.status === 'failed') {
          setTimeout(() => {
            setDownloadProgress(null);
          }, 8000);
        }
      }
    };

    socket.on('export-job-update', handleExportUpdate);

    return () => {
      socket.off('export-job-update', handleExportUpdate);
    };
  }, [socket, downloadProgress]);

  // Fetch employees with entries in the date range
  useEffect(() => {
    const fetchEmployees = async () => {
      if (currentView !== 'employees') return;
      
      setLoading(true);
      try {
        let queryParams = new URLSearchParams();
        
        if (debouncedSearchTerm) {
          queryParams.append('search', debouncedSearchTerm);
        }
        
        // Add filters to query params
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value);
          }
        });
        
        // First, get count to check if we need confirmation
        const countParams = new URLSearchParams(queryParams);
        countParams.append('count_only', 'true');
        countParams.append('group_by_employee', 'true');
        
        const countResponse = await fetch(`/api/labor?${countParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}` 
          }
        });
        
        if (countResponse.ok) {
          const countData = await countResponse.json();
          const totalRecords = countData.total || 0;
          setTotalCount(totalRecords);
          
          if (totalRecords > 10000) {
            setLoading(false);
            setShowLargeDatasetModal(true);
            return;
          }
        }
        
        queryParams.append('limit', '10000');
        queryParams.append('group_by_employee', 'true');
        
        const response = await fetch(`/api/labor?${queryParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}` 
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const data = await response.json();
        
        // Extract unique employees from entries
        const uniqueEmployees = {};
        (data.entries || []).forEach(entry => {
          if (!uniqueEmployees[entry.employee_id]) {
            uniqueEmployees[entry.employee_id] = {
              employee_id: entry.employee_id,
              full_name: entry.full_name,
              entry_count: 0,
              total_hours: 0
            };
          }
          uniqueEmployees[entry.employee_id].entry_count += 1;
          
          // Calculate hours if both clock_in and clock_out exist
          if (entry.clock_in_time && entry.clock_out_time) {
            const clockIn = new Date(entry.clock_in_time);
            const clockOut = new Date(entry.clock_out_time);
            const hours = (clockOut - clockIn) / (1000 * 60 * 60);
            uniqueEmployees[entry.employee_id].total_hours += hours;
          }
        });
        
        setEmployees(Object.values(uniqueEmployees));
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmployees();
  }, [currentView, debouncedSearchTerm, filters]);

  // Fetch employee timeline entries
  useEffect(() => {
    const fetchEmployeeEntries = async () => {
      if (currentView !== 'timeline' || !selectedEmployee) return;
      
      setLoading(true);
      try {
        let queryParams = new URLSearchParams();
        queryParams.append('employee_id', selectedEmployee.employee_id);
        
        // Add filters to query params
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value);
          }
        });
        
        // Check total count first for timeline view
        const countParams = new URLSearchParams(queryParams);
        countParams.append('count_only', 'true');
        
        const countResponse = await fetch(`/api/labor?${countParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}` 
          }
        });
        
        if (countResponse.ok) {
          const countData = await countResponse.json();
          const totalRecords = countData.total || 0;
          setTotalCount(totalRecords);
          
          if (totalRecords > 10000) {
            setLoading(false);
            setShowLargeDatasetModal(true);
            return;
          }
        }
        
        queryParams.append('limit', '10000');
        queryParams.append('sort_by', 'clock_in_time');
        queryParams.append('sort_direction', 'desc');
        
        const response = await fetch(`/api/labor?${queryParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}` 
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch employee entries');
        }
        
        const data = await response.json();
        const entries = data.entries || [];
        setEmployeeEntries(entries);
        setAllEntries(entries);
        setDisplayEntries(entries);
      } catch (error) {
        console.error('Error fetching employee entries:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmployeeEntries();
  }, [currentView, selectedEmployee, filters]);

  // Debounce search term
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Batched loading function
  const loadEntriesInBatches = async (baseParams, limit) => {
    setBatchLoading(true);
    setLoadedCount(0);
    
    const batchSize = 1000;
    const allLoadedEntries = [];
    let offset = 0;
    
    try {
      while (offset < limit) {
        const currentBatchSize = Math.min(batchSize, limit - offset);
        
        const batchParams = new URLSearchParams(baseParams);
        batchParams.append('limit', currentBatchSize.toString());
        batchParams.append('offset', offset.toString());
        batchParams.append('sort_by', 'clock_in_time');
        batchParams.append('sort_direction', 'desc');
        
        const response = await fetch(`/api/labor?${batchParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}` 
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch batch ${Math.floor(offset / batchSize) + 1}`);
        }
        
        const data = await response.json();
        const batchEntries = data.entries || [];
        
        allLoadedEntries.push(...batchEntries);
        offset += currentBatchSize;
        setLoadedCount(allLoadedEntries.length);
        
        // Small delay for smooth animation
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setEmployeeEntries(allLoadedEntries);
      setAllEntries(allLoadedEntries);
      setDisplayEntries(allLoadedEntries);
    } catch (error) {
      console.error('Error in batched loading:', error);
      throw error;
    } finally {
      setBatchLoading(false);
      setLoadedCount(0);
    }
  };

  // Handle large dataset confirmation
  const handleLargeDatasetConfirm = async (recordLimit) => {
    setSelectedRecordLimit(recordLimit);
    setShowLargeDatasetModal(false);
    setLoading(true);
    
    try {
      if (currentView === 'timeline' && selectedEmployee) {
        let queryParams = new URLSearchParams();
        queryParams.append('employee_id', selectedEmployee.employee_id);
        
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value);
          }
        });
        
        if (recordLimit === 'all') {
          await loadEntriesInBatches(queryParams, totalCount);
        } else {
          await loadEntriesInBatches(queryParams, parseInt(recordLimit));
        }
      }
    } catch (error) {
      console.error('Error in large dataset loading:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format functions
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    return formatInTimezone(dateTimeString, userTimezone, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: undefined
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString;
  };

  // Helper function to convert local timezone datetime to UTC for server storage
  const convertToUTC = (localDateTime) => {
    if (!localDateTime) return null;
    return new Date(localDateTime).toISOString();
  };

  // Helper function to convert UTC datetime from server to local timezone for editing
  const convertFromUTC = (utcDateTime) => {
    if (!utcDateTime) return '';
    const date = new Date(utcDateTime);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - (offset * 60 * 1000));
    return localDate.toISOString().slice(0, 16);
  };

  // Handle employee selection
  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setCurrentView('timeline');
  };

  // Handle back to employee list
  const handleBackToEmployees = () => {
    setCurrentView('employees');
    setSelectedEmployee(null);
    setSelectedEntry(null);
  };

  // Handle edit button click
  const handleEditClick = (e, entry) => {
    e.stopPropagation();
    setSelectedEntry(entry);
    setShowEditModal(true);
  };

  // Handle delete button click
  const handleDeleteClick = async (e, entryId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this entry? This cannot be undone.')) {
      try {
        const response = await fetch(`/api/labor/${entryId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete entry');
        }
        
        // Remove deleted entry from state
        setEmployeeEntries(employeeEntries.filter(entry => entry.id !== entryId));
        setSelectedEntry(null);
      } catch (error) {
        console.error('Error deleting entry:', error);
      }
    }
  };

  // Handle split button click
  const handleSplitClick = (e, entry) => {
    e.stopPropagation();
    
    if (!entry.clock_out_time) {
      alert("Cannot split an entry for an employee who is currently clocked in. Please wait until they clock out.");
      return;
    }
    
    setSelectedEntry(entry);
    setShowSplitModal(true);
  };

  // Handle save split
  const handleSaveSplit = async (splitData) => {
    try {
      console.log('Splitting entry:', splitData);
      
      // Step 1: Update the original entry
      const updateResponse = await fetch(`/api/labor/${splitData.originalEntryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(splitData.originalEntryUpdate)
      });
      
      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        console.error('Failed to update original entry:', errorText);
        throw new Error(`Failed to update original entry: ${updateResponse.status}`);
      }
      
      // Step 2: Create the new entry
      const createResponse = await fetch('/api/labor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(splitData.newEntry)
      });
      
      if (!createResponse.ok) {
        const errorText = await createResponse.text();
        console.error('Failed to create new entry:', errorText);
        throw new Error(`Failed to create new entry: ${createResponse.status}`);
      }
      
      // Refresh the entries list
      setFilters(prev => ({ ...prev }));
      
      setShowSplitModal(false);
      setSelectedEntry(null);
      alert("Entry split successfully!");
    } catch (error) {
      console.error('Error splitting entry:', error);
      alert(`Error splitting entry: ${error.message}`);
    }
  };

  // Handle save edit
  const handleSaveEdit = async (formData) => {
    try {
      console.log('Sending data:', formData);

      const response = await fetch(`/api/labor/${selectedEntry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      const updatedData = await response.json();

      // Update entries state
      setEmployeeEntries(employeeEntries.map(entry =>
        entry.id === updatedData.entry.id ? updatedData.entry : entry
      ));

      setShowEditModal(false);
      alert("Entry updated successfully!");
    } catch (error) {
      console.error('Error updating entry:', error);
      alert(`Error updating entry: ${error.message}`);
    }
  };

  // Export functions
  const openExportModal = () => {
    const dataToExport = currentView === 'timeline' ? employeeEntries : [];
    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Initialize export columns with all columns selected
    const initialExportColumns = {};
    allColumns.forEach(column => {
      initialExportColumns[column.id] = true;
    });
    setExportColumns(initialExportColumns);
    setShowExportModal(true);
  };

  const exportToCSV = async (selectedColumns) => {
    setExportLoading(true);

    try {
      // Build query parameters
      let queryParams = new URLSearchParams();

      if (debouncedSearchTerm) {
        queryParams.append('search', debouncedSearchTerm);
      }

      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });

      // Add employee filter for timeline view
      if (currentView === 'timeline' && selectedEmployee) {
        queryParams.append('employee_id', selectedEmployee.employee_id);
      }

      // Add selected columns as comma-separated string
      const selectedColumnIds = Object.entries(selectedColumns)
        .filter(([_, isSelected]) => isSelected)
        .map(([columnId, _]) => columnId);

      if (selectedColumnIds.length > 0) {
        queryParams.append('columns', selectedColumnIds.join(','));
      }

      // Close the export modal immediately
      setShowExportModal(false);
      setExportLoading(false);

      // Create export job
      const createResponse = await fetch(`/api/labor-export?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create export job');
      }

      const { jobId, filename } = await createResponse.json();

      // Initialize progress state
      setDownloadProgress({
        jobId,
        filename: filename || 'Preparing export...',
        progress: 0,
        bytesDownloaded: 0,
        totalBytes: 0,
        processedRows: 0,
        totalRows: 0,
        status: 'downloading'
      });

      // Poll for job status (WebSocket updates will also trigger updates)
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await fetch(`/api/labor-export/${jobId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (!statusResponse.ok) {
            clearInterval(pollInterval);
            throw new Error('Failed to get job status');
          }

          const jobStatus = await statusResponse.json();

          // Update progress
          setDownloadProgress(prev => ({
            ...prev,
            status: jobStatus.status === 'completed' ? 'completed' : 'downloading',
            progress: jobStatus.progress || 0,
            processedRows: jobStatus.processedRows || 0,
            totalRows: jobStatus.totalRows || 0,
            filename: jobStatus.filename || prev.filename,
            error: jobStatus.error
          }));

          // If completed, download the file
          if (jobStatus.status === 'completed') {
            clearInterval(pollInterval);

            // Fetch the file with authentication
            const fileResponse = await fetch(`/api/labor-export/${jobId}/download`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });

            if (fileResponse.ok) {
              // Convert to blob and download
              const blob = await fileResponse.blob();
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', jobStatus.filename);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              // Update UI to show completion
              setDownloadProgress(prev => ({
                ...prev,
                status: 'completed',
                progress: 100
              }));

              // Auto-close after 3 seconds
              setTimeout(() => {
                setDownloadProgress(null);
              }, 3000);
            } else {
              throw new Error('Failed to download file');
            }
          }

          // If failed, stop polling
          if (jobStatus.status === 'failed') {
            clearInterval(pollInterval);
            setDownloadProgress(prev => ({
              ...prev,
              status: 'error',
              error: jobStatus.error || 'Export failed'
            }));

            // Auto-close after 8 seconds
            setTimeout(() => {
              setDownloadProgress(null);
            }, 8000);
          }
        } catch (error) {
          clearInterval(pollInterval);
          console.error('Error polling job status:', error);
        }
      }, 2000); // Poll every 2 seconds

      // Store interval ID for cleanup
      abortControllerRef.current = { pollInterval };

    } catch (error) {
      console.error('Error exporting to CSV:', error);

      let errorMessage = 'Failed to export data. Please try again.';

      if (error.name === 'AbortError') {
        errorMessage = 'Download cancelled by user';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Try reducing the date range or applying more filters.';
      } else if (error.message.includes('HTTP 401')) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.message.includes('HTTP 403')) {
        errorMessage = 'You do not have permission to export this data.';
      } else if (error.message.includes('HTTP 500')) {
        errorMessage = 'Server error. Please contact support if this persists.';
      } else if (error.message) {
        errorMessage = `Export failed: ${error.message}`;
      }

      setDownloadProgress(prev => prev ? {
        ...prev,
        status: 'error',
        error: errorMessage
      } : {
        filename: 'Export failed',
        progress: 0,
        bytesDownloaded: 0,
        totalBytes: 0,
        status: 'error',
        error: errorMessage
      });

      // Auto-close error toast after 8 seconds
      setTimeout(() => {
        setDownloadProgress(null);
      }, 8000);
    } finally {
      setExportLoading(false);
    }
  };

  const handleCancelDownload = async () => {
    if (abortControllerRef.current && abortControllerRef.current.pollInterval) {
      clearInterval(abortControllerRef.current.pollInterval);
    }

    if (downloadProgress && downloadProgress.jobId) {
      try {
        await fetch(`/api/labor-export/${downloadProgress.jobId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      } catch (error) {
        console.error('Error cancelling export:', error);
      }
    }

    setDownloadProgress(null);
  };

  const handleCloseProgress = () => {
    if (downloadProgress?.status !== 'downloading') {
      setDownloadProgress(null);
    }
  };

  const handleExportColumnToggle = (columnId) => {
    setExportColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  const handleSelectAllExportColumns = () => {
    const allSelected = {};
    allColumns.forEach(column => {
      allSelected[column.id] = true;
    });
    setExportColumns(allSelected);
  };

  const handleDeselectAllExportColumns = () => {
    const allDeselected = {};
    allColumns.forEach(column => {
      allDeselected[column.id] = false;
    });
    setExportColumns(allDeselected);
  };

  const handleConfirmExport = () => {
    exportToCSV(exportColumns);
    setShowExportModal(false);
  };

  // Helper function to get display text for current date range
  const getDateRangeDisplayText = () => {
    switch (dateRangeType) {
      case 'today':
        return currentDateView.displayDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      case 'past7days':
        const sevenDaysAgo = new Date(currentDateView.start);
        return `${sevenDaysAgo.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${currentDateView.displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'pastmonth':
        const oneMonthAgo = new Date(currentDateView.start);
        return `${oneMonthAgo.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${currentDateView.displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'custom':
        const customStart = new Date(currentDateView.start);
        const customEnd = new Date(currentDateView.end);
        return `${customStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${customEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      default:
        return currentDateView.displayDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6 h-full flex flex-col overflow-hidden">
      {/* Header with controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          {currentView === 'timeline' && (
            <button
              onClick={handleBackToEmployees}
              className="bg-slate-700 text-slate-200 border border-slate-600 rounded-md py-2 px-3 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <div className="flex items-center">
                <ChevronLeft className="h-5 w-5 mr-1" />
                <span>Back to Employees</span>
              </div>
            </button>
          )}
          
          <div className="flex items-center bg-slate-700 text-slate-200 border border-slate-600 rounded-md p-2">
            <Calendar className="h-5 w-5 mr-2 text-blue-400" />
            <span className="font-medium">
              {getDateRangeDisplayText()}
            </span>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => handleDateRangeChange('today')}
              className={`rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                dateRangeType === 'today' 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600'
              }`}
            >
              <div className="flex items-center">
                <CalendarDays className="h-5 w-5 mr-1" />
                <span>Today</span>
              </div>
            </button>
            
            <button
              onClick={() => handleDateRangeChange('past7days')}
              className={`rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                dateRangeType === 'past7days' 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600'
              }`}
            >
              <div className="flex items-center">
                <CalendarRange className="h-5 w-5 mr-1" />
                <span>Past 7 Days</span>
              </div>
            </button>
            
            <button
              onClick={() => handleDateRangeChange('pastmonth')}
              className={`rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                dateRangeType === 'pastmonth' 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600'
              }`}
            >
              <div className="flex items-center">
                <CalendarX2 className="h-5 w-5 mr-1" />
                <span>Past Month</span>
              </div>
            </button>
            
            {/* Custom Date Range Selector */}
            <div className="flex items-center space-x-2 bg-slate-700 border border-slate-600 rounded-md p-2">
              <div className="flex items-center space-x-2">
                <label className="text-slate-300 text-sm">From:</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-slate-300 text-sm">To:</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-slate-600 text-slate-200 border border-slate-500 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <button
                onClick={handleCustomDateRangeSearch}
                className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <div className="flex items-center">
                  <Search className="h-4 w-4 mr-1" />
                  <span>Search</span>
                </div>
              </button>
            </div>
          </div>
          
          {currentView === 'timeline' && selectedEmployee && (
            <div className="bg-slate-700 text-slate-300 border border-slate-600 rounded-md py-2 px-3">
              <span>{employeeEntries.length} entries for {selectedEmployee.full_name}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {currentView === 'timeline' && (
            <button
              onClick={openExportModal}
              className="bg-green-600 text-white border border-green-500 rounded-md py-2 px-4 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center space-x-2"
              title="Export data to CSV"
            >
              <Download className="h-5 w-5" />
              <span>Export CSV</span>
            </button>
          )}
          <div className="relative">
            <input
              type="text"
              placeholder={currentView === 'employees' ? "Search employees..." : "Search entries..."}
              className="bg-slate-700 text-slate-200 border border-slate-600 rounded-md py-2 px-4 pl-10 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
          </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div className="flex-1 overflow-auto border border-slate-700 rounded-lg">
        {currentView === 'employees' ? (
          // Employee List View
          <div className="p-4">
            <h2 className="text-xl font-semibold text-slate-200 mb-4 flex items-center">
              <User className="h-6 w-6 mr-2 text-blue-400" />
              Employees with Activity
            </h2>
            
            {loading ? (
              <div className="text-center py-8 text-slate-400">Loading employees...</div>
            ) : employees.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No employees found for this date range</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {employees.sort((a, b) => a.full_name.localeCompare(b.full_name)).map((employee) => (
                  <div
                    key={employee.employee_id}
                    onClick={() => handleEmployeeClick(employee)}
                    className="bg-slate-700 border border-slate-600 rounded-lg p-4 hover:bg-slate-600 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-medium text-slate-200">{employee.full_name}</h3>
                      <span className="text-sm text-slate-400">ID: {employee.employee_id}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-300">
                      <span>{employee.entry_count} entries</span>
                      <span>{employee.total_hours.toFixed(1)}h total</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Timeline View
          <div className="p-4">
            <h2 className="text-xl font-semibold text-slate-200 mb-4 flex items-center">
              <Clock className="h-6 w-6 mr-2 text-green-400" />
              Timeline for {selectedEmployee?.full_name}
            </h2>
            
            {loading ? (
              <div className="text-center py-8 text-slate-400">Loading timeline...</div>
            ) : employeeEntries.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No entries found for this employee in the selected date range</div>
            ) : (
              <div className="bg-slate-700 border border-slate-600 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-800 border-b border-slate-600">
                      <th className="py-3 px-4 text-left font-medium text-slate-300 text-sm" style={{ width: '160px' }}>Full Name</th>
                      <th className="py-3 px-4 text-left font-medium text-slate-300 text-sm" style={{ width: '120px' }}>Activity</th>
                      <th className="py-3 px-4 text-left font-medium text-slate-300 text-sm" style={{ width: '120px' }}>Business Unit</th>
                      <th className="py-3 px-4 text-left font-medium text-slate-300 text-sm" style={{ width: '100px' }}>Project</th>
                      <th className="py-3 px-4 text-left font-medium text-slate-300 text-sm" style={{ width: '160px' }}>Clock In</th>
                      <th className="py-3 px-4 text-left font-medium text-slate-300 text-sm" style={{ width: '160px' }}>Clock Out</th>
                      <th className="py-3 px-4 text-left font-medium text-slate-300 text-sm" style={{ width: '100px' }}>Total Time</th>
                      <th className="py-3 px-4 text-right font-medium text-slate-300 text-sm" style={{ width: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-600">
                    {displayEntries
                      .sort((a, b) => new Date(b.clock_in_time) - new Date(a.clock_in_time))
                      .map((entry, index) => (
                        <tr
                          key={entry.id}
                          className={`text-sm hover:bg-slate-600 transition-colors ${
                            index % 2 === 0 ? 'bg-slate-750' : 'bg-slate-700'
                          }`}
                        >
                          <td className="py-3 px-4 text-slate-200 font-medium truncate" style={{ width: '160px' }} title={entry.full_name}>
                            {entry.full_name}
                          </td>
                          <td className="py-3 px-4 text-slate-200 truncate" style={{ width: '120px' }} title={entry.activity || 'N/A'}>
                            {entry.activity || '-'}
                          </td>
                          <td className="py-3 px-4 text-slate-200 truncate" style={{ width: '120px' }} title={entry.business_unit || 'N/A'}>
                            {entry.business_unit || '-'}
                          </td>
                          <td className="py-3 px-4 text-slate-200 truncate" style={{ width: '100px' }} title={entry.project || 'N/A'}>
                            {entry.project || '-'}
                          </td>
                          <td className="py-3 px-4 text-slate-200 truncate" style={{ width: '160px' }} title={formatDateTime(entry.clock_in_time)}>
                            {formatDateTime(entry.clock_in_time)}
                          </td>
                          <td className="py-3 px-4 text-slate-200 truncate" style={{ width: '160px' }} title={formatDateTime(entry.clock_out_time)}>
                            {formatDateTime(entry.clock_out_time)}
                          </td>
                          <td className="py-3 px-4 text-slate-200 truncate" style={{ width: '100px' }} title={entry.total_time || 'N/A'}>
                            {entry.total_time || 'N/A'}
                          </td>
                          <td className="py-3 px-4" style={{ width: '120px' }}>
                            <div className="flex items-center justify-end space-x-1">
                              <button 
                                onClick={(e) => handleEditClick(e, entry)}
                                className="p-1 hover:bg-slate-500 rounded transition-colors"
                                title="Edit"
                              >
                                <Edit className="h-3 w-3 text-blue-400" />
                              </button>
                              <button 
                                onClick={(e) => handleSplitClick(e, entry)}
                                className={`p-1 rounded transition-colors ${entry.clock_out_time ? 'hover:bg-slate-500' : 'opacity-50 cursor-not-allowed'}`}
                                title={entry.clock_out_time ? "Split" : "Cannot split - employee is still clocked in"}
                                disabled={!entry.clock_out_time}
                              >
                                <Scissors className={`h-3 w-3 ${entry.clock_out_time ? 'text-green-400' : 'text-slate-500'}`} />
                              </button>
                              <button 
                                onClick={(e) => handleDeleteClick(e, entry.id)}
                                className="p-1 hover:bg-slate-500 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-3 w-3 text-red-400" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Batch Loading Modal */}
      {batchLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-8 w-full max-w-md">
            <div className="text-center">
              <Loader2 className="h-12 w-12 text-blue-400 animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-200 mb-2">Loading Records</h3>
              <p className="text-slate-400 mb-4">
                Loaded {loadedCount.toLocaleString()} of {selectedRecordLimit?.toLocaleString() || totalCount.toLocaleString()} records
              </p>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ 
                    width: `${(loadedCount / (selectedRecordLimit || totalCount)) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Large Dataset Confirmation Modal */}
      {showLargeDatasetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6 w-full max-w-lg">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-yellow-400 mr-3" />
              <h3 className="text-xl font-semibold text-slate-200">Large Dataset Detected</h3>
            </div>
            
            <p className="text-slate-300 mb-4">
              This query will return {totalCount.toLocaleString()} records, which may take some time to load. 
              Would you like to proceed or limit the number of records?
            </p>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => handleLargeDatasetConfirm(1000)}
                className="bg-slate-700 text-slate-200 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Load 1,000
              </button>
              <button
                onClick={() => handleLargeDatasetConfirm(2500)}
                className="bg-slate-700 text-slate-200 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Load 2,500
              </button>
              <button
                onClick={() => handleLargeDatasetConfirm(5000)}
                className="bg-slate-700 text-slate-200 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Load 5,000
              </button>
              <button
                onClick={() => handleLargeDatasetConfirm(7500)}
                className="bg-slate-700 text-slate-200 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Load 7,500
              </button>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => handleLargeDatasetConfirm('all')}
                className="flex-1 bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                Load All ({totalCount.toLocaleString()})
              </button>
              <button
                onClick={() => setShowLargeDatasetModal(false)}
                className="flex-1 bg-slate-600 text-slate-200 px-4 py-2 rounded hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {showEditModal && selectedEntry && (
        <EditEntryModal 
          entry={selectedEntry} 
          onSave={handleSaveEdit}
          onCancel={() => setShowEditModal(false)}
          convertFromUTC={convertFromUTC}
          convertToUTC={convertToUTC}
        />
      )}
      
      {/* Split Modal */}
      {showSplitModal && selectedEntry && (
        <SplitEntryModal
          entry={selectedEntry}
          onSave={handleSaveSplit}
          onCancel={() => setShowSplitModal(false)}
          userTimezone={userTimezone}
          convertToUTC={convertToUTC}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          exportColumns={exportColumns}
          allColumns={allColumns}
          onToggleColumn={handleExportColumnToggle}
          onSelectAll={handleSelectAllExportColumns}
          onDeselectAll={handleDeselectAllExportColumns}
          onConfirm={handleConfirmExport}
          onCancel={() => setShowExportModal(false)}
          entriesCount={employeeEntries.length}
          isLoading={exportLoading}
        />
      )}

      {/* Download Progress Toast */}
      {downloadProgress && (
        <DownloadProgressToast
          filename={downloadProgress.filename}
          progress={downloadProgress.progress}
          bytesDownloaded={downloadProgress.bytesDownloaded}
          totalBytes={downloadProgress.totalBytes}
          processedRows={downloadProgress.processedRows}
          totalRows={downloadProgress.totalRows}
          status={downloadProgress.status}
          error={downloadProgress.error}
          onCancel={handleCancelDownload}
          onClose={handleCloseProgress}
        />
      )}
    </div>
  );
};

// Edit Modal Component (reusing from ClockInEntriesTab)
const EditEntryModal = ({ entry, onSave, onCancel, convertFromUTC, convertToUTC }) => {
  const [formData, setFormData] = useState({
    activity: entry.activity || '',
    business_unit: entry.business_unit || '',
    group: entry.group || '',
    project: entry.project || '',
    shift: entry.shift || '',
    team: entry.team || '',
    location: entry.location || '',
    notes: entry.notes || '',
    reference1: entry.reference1 || '',
    reference2: entry.reference2 || '',
    reference3: entry.reference3 || '',
    date: entry.date || '',
    clock_in_time: convertFromUTC(entry.clock_in_time),
    clock_out_time: convertFromUTC(entry.clock_out_time),
  });

  const [activities, setActivities] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [workGroups, setWorkGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        const [activitiesRes, businessUnitsRes, projectsRes, shiftsRes, teamsRes, workGroupsRes] = await Promise.all([
          fetch('/api/activities', { headers }),
          fetch('/api/business-units', { headers }),
          fetch('/api/projects', { headers }),
          fetch('/api/shifts', { headers }),
          fetch('/api/teams', { headers }),
          fetch('/api/work-groups', { headers })
        ]);

        if (activitiesRes.ok) setActivities(await activitiesRes.json());
        if (businessUnitsRes.ok) setBusinessUnits(await businessUnitsRes.json());
        if (projectsRes.ok) setProjects(await projectsRes.json());
        if (shiftsRes.ok) setShifts(await shiftsRes.json());
        if (teamsRes.ok) setTeams(await teamsRes.json());
        if (workGroupsRes.ok) setWorkGroups(await workGroupsRes.json());
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDropdownOptions();
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const dataToSend = {
      ...formData,
      clock_in_time: formData.clock_in_time ? convertToUTC(formData.clock_in_time) : null,
      clock_out_time: formData.clock_out_time ? convertToUTC(formData.clock_out_time) : null,
    };
    
    onSave(dataToSend);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-slate-200 mb-4">Edit Entry #{entry.id}</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Read-only fields */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">ID</label>
              <input 
                type="text" 
                value={entry.id} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Employee ID</label>
              <input 
                type="text" 
                value={entry.employee_id} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Full Name</label>
              <input 
                type="text" 
                value={entry.full_name} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Total Time</label>
              <input 
                type="text" 
                value={entry.total_time || 'N/A'} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            {/* Editable fields */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Activity</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="activity"
                  value={formData.activity}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select an activity</option>
                  {activities.filter(activity => activity.is_active).map(activity => (
                    <option key={activity.id} value={activity.name}>
                      {activity.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Business Unit</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="business_unit"
                  value={formData.business_unit}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a business unit</option>
                  {businessUnits.filter(unit => unit.is_active).map(unit => (
                    <option key={unit.id} value={unit.identifier}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Group</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="group"
                  value={formData.group || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a group</option>
                  {workGroups.filter(group => group.is_active).map(group => (
                    <option key={group.id} value={group.name}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Project</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="project"
                  value={formData.project || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a project</option>
                  {projects.filter(project => project.is_active).map(project => (
                    <option key={project.id} value={project.name}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Shift</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="shift"
                  value={formData.shift || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a shift</option>
                  {shifts.map(shift => (
                    <option key={shift.id} value={shift.name}>
                      {shift.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Team</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="team"
                  value={formData.team || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Location</label>
              <input 
                type="text" 
                name="location"
                value={formData.location} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Date</label>
              <input 
                type="date" 
                name="date"
                value={formData.date} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Clock In Time</label>
              <input 
                type="datetime-local" 
                name="clock_in_time"
                value={formData.clock_in_time} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Clock Out Time</label>
              <input 
                type="datetime-local" 
                name="clock_out_time"
                value={formData.clock_out_time} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Reference 1</label>
              <input 
                type="text" 
                name="reference1"
                value={formData.reference1} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Reference 2</label>
              <input 
                type="text" 
                name="reference2"
                value={formData.reference2} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Reference 3</label>
              <input 
                type="text" 
                name="reference3"
                value={formData.reference3} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-400 mb-1">Notes</label>
              <textarea 
                name="notes"
                value={formData.notes} 
                onChange={handleChange}
                rows="3"
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onCancel}
              className="bg-slate-700 text-slate-300 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeTab;
